import { Button, type ButtonProps, cn } from '@seen/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import { type JSX, type ReactNode, type RefObject, useMemo } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { usePractitionersInternal } from '#src/hooks/usePractitionersInternal.ts'

interface OwnerRequiredComboboxButtonProps
  extends
    Omit<ButtonProps, 'value' | 'onChange' | 'size'>,
    VariantProps<typeof variants> {
  value: string
  onChange: (value: string) => any
  ref?: RefObject<HTMLButtonElement>
}

export function OwnerRequiredComboboxButton({
  value,
  onChange,
  className,
  size,
  ...rest
}: OwnerRequiredComboboxButtonProps): JSX.Element {
  const options = useOwnerRequiredComboboxOptions(value)
  return (
    <Combobox options={options} onSelect={onChange}>
      <Button
        variant="outline"
        className={cn(variants({ size, className }))}
        size={size}
        {...rest}
      >
        {value && (
          <PractitionerNameplate
            practitioner={value}
            size={size === 'xs' ? 3 : 5}
          />
        )}
        {!value && (
          <UserPlaceholderNameplate size={size}>Owner</UserPlaceholderNameplate>
        )}
      </Button>
    </Combobox>
  )
}

const variants = cva('flex items-center shadow-sm', {
  variants: {
    size: {
      default: 'gap-2',
      xs: 'gap-1',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

function useOwnerRequiredComboboxOptions(
  selectedValue: string
): { value: string; label: ReactNode; keywords: string[] }[] {
  const practitioners = usePractitionersInternal()
  const options = useMemo(
    () =>
      practitioners.map((p) => ({
        value: p.reference.reference,
        label: (
          <PractitionerOption
            practitioner={p.reference}
            isSelected={p.reference.reference === selectedValue}
          />
        ),
        keywords: p.searchKeywords,
      })),
    [practitioners, selectedValue]
  )
  return options
}
